import { PatientIdentity } from '@lifen-integration/features/patientIdentities/types/index';
import { UserSettings } from '../auth/user.type';
import { Attachment } from './attachment';
import { DocumentSource } from './documentSource.types';
import { IrenePrediction } from './irenePrediction';
import { IOriginalSender, Organization } from './organization';
import { Patient } from './patient.types';
import { PatientSearchDraft } from './patientSearchDraft';
import { Practitioner } from './practitioner';
import { Professional, ProfessionalDto } from './professional';
import {
  DocumentBase,
  DocumentPatient,
  DocumentPatientDto,
  DocumentVerificationV2,
  FlowType,
} from './sendingRequest.types';
import { ISigningPractitioner } from './signingPractitioner.types';

export interface DeliveryFeedback {
  temporaryError: boolean;
  errorMessage: string;
}

export interface Sending {
  delivery_error: string;
  delivery_error_message: string;
}

interface IntegrationRequestBase extends DocumentBase {
  uuid?: string;
  documentType?: string;
  folder?: string;
  medicalExamType?: string;
  originalSenderOrganization?: IOriginalSender;
  originalDocumentId?: string;
  /**
   * Rails id of the first attachment — an integer, not the FHIR uuid `Attachment.id` carries. Key
   * for `GET /api/v2/document_attachments/:id`, served identically by v1 and v2.
   */
  documentAttachmentId?: number;
  fromSplit?: boolean;
  verificationStatus: IntegrationRequestVerificationStatus;
  hasCda?: boolean;
  ehrDeliveryRejected?: boolean;
  predictedDocumentType?: string;
  verificationsV2?: DocumentVerificationV2[];
  predictedDocumentSpeciality?: string;
  documentSpeciality?: string;
  forwardToLifenDocument: boolean;
  forwardToLifenDocumentId?: string;
  forwardableToLifenDocument: boolean;
  nonForwardableToLifenDocumentReason?: string;
  merged?: boolean;
  medicalVisaNeeded?: boolean;
  assignedPractitionerLifenId?: string | null;
  processedWithNewPatientFlow?: boolean;
  patientIdentity?: PatientIdentity;
  patientSearchDraft?: PatientSearchDraft | null;
  deliveryFeedback?: DeliveryFeedback;
  sending?: Sending;
}

export interface IntegrationRequestDto extends IntegrationRequestBase {
  sender: ProfessionalDto;
  patient?: DocumentPatientDto;
  originalSenderPractitioner?: ISigningPractitioner;
  source?: DocumentSource;
  irenePrediction?: IrenePrediction;
  possibleDuplicates?: string[];
}

export interface IntegrationRequest extends IntegrationRequestBase {
  sender: Professional;
  patient?: DocumentPatient;
  attachments?: Attachment[];
  originalSenderPractitioner?: ISigningPractitioner;
  source?: DocumentSource;
  possibleDuplicates?: string[];
  irenePrediction?: IrenePrediction;
}

export enum IntegrationsDashboardType {
  Worklist = 'worklist',
  Integrated = 'integrated',
}

export const isIntegrationsDashboardType = (val: unknown): val is IntegrationsDashboardType =>
  typeof val === 'string' &&
  Object.values(IntegrationsDashboardType).includes(val as IntegrationsDashboardType);

export interface IntegrationRequestBundle {
  document: IntegrationRequestDto;
  attachments?: Attachment[]; // TODO: delete
  entities: {
    patients: Patient[];
    organizations: Organization[];
    practitioners: Practitioner[];
    attachments?: Attachment[];
  };
}

export interface CreateIntegrationRequest {
  requesterId: string;
  sender: ProfessionalDto;
  folder?: string;
  file: File;
  flowType: FlowType;
  userSettings: UserSettings;
}

export enum IntegrationRequestVerificationStatus {
  ReadyToImport = 'READY_TO_IMPORT',
  UserVerificationNeeded = 'USER_VERIFICATION_NEEDED',
  Waiting = 'WAITING',
  Integrating = 'INTEGRATING',
  Sent = 'SENT',
  Error = 'ERROR',
  EhrError = 'EHR_ERROR',
  Delivered = 'DELIVERED',
  PossibleDuplicate = 'POSSIBLE_DUPLICATE',
}

export type DeliveryStatus = Exclude<
  IntegrationRequestVerificationStatus,
  | IntegrationRequestVerificationStatus.Waiting
  | IntegrationRequestVerificationStatus.UserVerificationNeeded
  | IntegrationRequestVerificationStatus.ReadyToImport
  | IntegrationRequestVerificationStatus.PossibleDuplicate
>;

export const DeliveryStatus: Record<DeliveryStatus, DeliveryStatus> = {
  INTEGRATING: IntegrationRequestVerificationStatus.Integrating,
  SENT: IntegrationRequestVerificationStatus.Sent,
  ERROR: IntegrationRequestVerificationStatus.Error,
  EHR_ERROR: IntegrationRequestVerificationStatus.EhrError,
  DELIVERED: IntegrationRequestVerificationStatus.Delivered,
};
